/** The four figures a driver reads before they read anything else.
 *
 * Best lap, the theoretical lap, the spread, and how many laps there were.
 * Only the first is in the accent, because it is the only one that is an
 * achievement — the other three are context, and colouring context makes the
 * achievement harder to find.
 *
 * Each card says what its number is measured over, in its own footer, because
 * all three of the derived ones are arguable and a figure whose method is
 * invisible invites the wrong argument. Where there is no answer the card says
 * an em dash and explains why underneath, rather than printing a confident
 * zero: a stint with one flying lap has no spread, and that is a different
 * fact from a stint that was perfectly consistent.
 */

import { Card, CardMeta, Kicker } from '@/components/ui';
import { DASH, count, lapTime } from '@/lib/format';
import { syncColour } from '@/components/sessions/derive';
import type { SyncState } from '@/components/sessions/derive';
import type { ReactNode } from 'react';

export interface SummaryCardsProps {
  bestMs: number | null;
  bestLapNumber: number | null;
  theoreticalMs: number | null;
  consistencyMs: number | null;
  /** How many laps the spread was taken over, for the footer. */
  consistencyOver: number;
  laps: number;
  sync: SyncState;
}

function Figure({
  kicker,
  value,
  accent,
  meta,
}: {
  kicker: string;
  value: string;
  accent?: boolean;
  meta: ReactNode;
}) {
  return (
    <Card>
      <Kicker>{kicker}</Kicker>
      <span
        className="tabular-nums"
        style={{
          fontSize: 26,
          lineHeight: 1,
          color: accent ? 'var(--color-accent-300)' : 'var(--color-text)',
        }}
      >
        {value}
      </span>
      <CardMeta>{meta}</CardMeta>
    </Card>
  );
}

/** `0.31 s` — two decimals, which is the resolution consistency is argued at. */
function spread(ms: number | null): string {
  return ms === null ? DASH : `${(ms / 1000).toFixed(2)} s`;
}

export function SummaryCards({
  bestMs,
  bestLapNumber,
  theoreticalMs,
  consistencyMs,
  consistencyOver,
  laps,
  sync,
}: SummaryCardsProps) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
        gap: 10,
      }}
    >
      <Figure
        kicker="best lap"
        value={lapTime(bestMs)}
        accent
        meta={bestLapNumber === null ? 'no lap counted yet' : `lap ${bestLapNumber}`}
      />
      <Figure
        kicker="theoretical"
        value={lapTime(theoreticalMs)}
        meta={theoreticalMs === null ? 'sectors not timed' : 'best sectors combined'}
      />
      <Figure
        kicker="consistency"
        value={spread(consistencyMs)}
        meta={
          consistencyMs === null
            ? 'needs two laps after the first'
            : `σ over ${count(consistencyOver)} laps, less the first`
        }
      />
      <Figure
        kicker="laps"
        value={count(laps)}
        meta={<span style={{ color: syncColour(sync) }}>{sync.label}</span>}
      />
    </div>
  );
}

export default SummaryCards;
